import React, { useState, useEffect } from 'react';
import styles from '@/common/Layout/index.module.less';
import {
  Row,
  Col,
  Card,
  Radio,
  Space,
  Input,
  Form,
  Empty,
  message,
  Select,
  Tag,
  Tooltip
} from 'antd';
import Layout from '@/common/Layout';
import { useDispatch } from 'react-redux';
import {
  getAllContainerRooms,
  getAllContainers,
  createLabels,
  scanContainers,
  assignContainerBins
} from '../../redux/slice';
import { getContainers } from '@/features/MoveMaterials/redux/slice';
import { QrScanSingleShotModal, QrScanChecklistModal } from '@/common/QrScan';
import { QrcodeOutlined } from '@ant-design/icons';
import CustomButton from '@/common/CustomButton';
import FormItem from '@/common/FormItem';
import CustomTable from '@/common/Content/CustomTable';
import { useLocation } from 'react-router-dom';
import { history } from '@/app/history';
import CustomModal from '@/common/CustomModal';
import moment from 'moment';

const registerLogBooks = ({ loading }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [credForm] = Form.useForm();
  const [openCam, setOpenCam] = useState(false);
  const [openScanChecklist, setOpenScanChecklist] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [roomNum, setRoomNum] = useState('');
  const [containersData, setContainersData] = useState([]);
  const [scannedIds, setScannedIds] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showCredModal, setShowCredModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [binOptions, setBinOptions] = useState([]);
  const [selectedBin, setSelectedBin] = useState(undefined);
  const [fetchSeed, setFetchSeed] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const searchParam = useLocation().search;
  const searchParams = new URLSearchParams(searchParam);
  const type = searchParams.get('type');
  const typeLower = (type || '').toLowerCase();

  // Scan applies to inward, outward and return. Bin-location grouping is
  // inward-WH (assign) and outward (single-location) only — return shows the
  // location for reference but does not enforce grouping.
  const isReturn = typeLower === 'return';
  const scanEnabled =
    typeLower === 'inward' || typeLower === 'outward' || typeLower === 'return';

  const labels = containersData?.[0]?.prod_dispensing_labels || [];
  const dispenseId = containersData?.[0]?.id;

  const normalizeRoom = (room_no) =>
    (room_no || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const selectedRoom = rooms.find((r) => r.id === roomNum);
  const isWH = normalizeRoom(selectedRoom?.room_no) === 'WH';
  // Inward behaviour is driven by the room's capability flags (WH + WIP 6 are
  // seeded true). Fall back to the legacy WH string match when the flags are
  // absent (backend migration not yet applied) so WH never loses its bin /
  // verification behaviour during rollout.
  const roomRequiresBin = selectedRoom?.requires_bin ?? isWH;
  const roomRequiresVerification = selectedRoom?.requires_verification ?? isWH;

  const getLocation = (label) =>
    label?.prod_bin_locations?.[0]?.current_location || '';
  const isScanned = (id) => scannedIds.includes(id);
  // A container counts as "inwarded" (blocking re-inward) only while it is
  // currently inwarded — once returned it can be inwarded again.
  const isInwarded = (label) => !!label?.inwarded_by && !label?.is_returned;
  // Block re-inward of a dispense whose containers are already inwarded.
  const alreadyInwarded =
    typeLower === 'inward' && labels.length > 0 && labels.every(isInwarded);
  // Outward of warehouse-located containers is grouped by bin location.
  const isLocationBased =
    typeLower === 'outward' && labels.some((l) => getLocation(l));

  useEffect(() => {
    handleGetAllContainerRooms();
  }, []);

  const handleGetAllContainerRooms = () => {
    dispatch(getAllContainerRooms()).then((res) => {
      if (res?.payload?.success) {
        setRooms(res?.payload?.data);
      }
    });
  };

  // Seed local scanned state from persisted flags so progress rehydrates.
  const seedScannedFromLabels = (data) => {
    if (!scanEnabled) {
      setScannedIds([]);
      return;
    }
    const fetchedLabels = data?.[0]?.prod_dispensing_labels || [];
    const scannedField =
      typeLower === 'inward'
        ? 'inward_scanned'
        : typeLower === 'outward'
        ? 'outward_scanned'
        : 'return_scanned';
    const already = fetchedLabels
      .filter((l) => l[scannedField])
      .map((l) => l.id);
    setScannedIds(already);
  };

  const persistScan = async (labelIds, dispId) => {
    const ids = (labelIds || []).filter(Boolean);
    if (!ids.length) return;
    const res = await dispatch(
      scanContainers({
        prod_dispenses_id: dispId || dispenseId,
        scan_type: typeLower,
        prod_dispense_label_ids: ids
      })
    );
    if (res?.payload?.success) {
      setScannedIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      message.error(res?.payload?.message || 'Failed to save scan status');
    }
  };

  const refetchContainers = async (seed) => {
    const container = seed || fetchSeed;
    if (!container) return;
    const res = await dispatch(
      getAllContainers({ prod_containers: container, type: typeLower })
    );
    if (res?.payload?.success) {
      setContainersData(res?.payload?.data);
      seedScannedFromLabels(res?.payload?.data);
    }
  };

  const handleOpenScan = () => {
    if (typeLower === 'inward' && !roomNum) {
      message.error('Please select a room before scanning');
      return;
    }
    setOpenCam(true);
  };

  // Initial fetch scan — loads all containers of the dispense. For inward/
  // outward it also auto-marks the scanned container.
  const handleQrScan = async (scanData) => {
    if (!scanData || scanData === '') return;
    const code = scanData?.text;
    const payload = { prod_containers: code, type: typeLower };
    const res = await dispatch(getAllContainers(payload));
    setOpenCam(false);
    if (!res?.payload?.success) {
      message.error(res?.payload?.message || 'Failed to load container');
      return;
    }
    // Container is in a state that doesn't apply to this register —
    // tell the user why instead of silently loading siblings/empty.
    if (res?.payload?.valid_for_scan === false) {
      message.warning(res?.payload?.message);
      return;
    }
    const data = res?.payload?.data;
    if (!data || !data.length) {
      message.warning(res?.payload?.message || `Container ${code} not found.`);
      return;
    }
    setContainersData(data);
    setFetchSeed(code);
    seedScannedFromLabels(data);
    if (scanEnabled) {
      const fetchedLabels = data?.[0]?.prod_dispensing_labels || [];
      const matched = fetchedLabels.find(
        (l) =>
          String(l.prod_containers).toUpperCase() === String(code).toUpperCase()
      );
      // Don't re-scan an already-inwarded container.
      if (matched && !(typeLower === 'inward' && isInwarded(matched))) {
        await persistScan([matched.id], data?.[0]?.id);
      }
    }
  };

  // Each container is saved live (onItemConfirmed) as it is scanned, so the
  // Save button just closes the modal.
  const handleChecklistClose = () => {
    setOpenScanChecklist(false);
  };

  // Live persistence — fired by the modal on each new scan.
  const handleItemConfirmed = (label) => {
    const matched = labels.find(
      (l) =>
        String(l.prod_containers).toUpperCase() === String(label).toUpperCase()
    );
    if (matched && !isScanned(matched.id)) {
      persistScan([matched.id]);
    }
  };

  const onSearchbin = (value) => {
    if (value?.length > 2) {
      // Pass the selected room so the backend scopes bins by its area
      // (WH → both bin types, WIP 5/6 → production bins).
      dispatch(getContainers({ id: value, prod_room_id: roomNum })).then(
        (response) => {
          if (response?.payload?.success) {
            setBinOptions(response?.payload?.data || []);
          }
        }
      );
    }
  };

  const handleAssignBin = async () => {
    if (!selectedBin || !selectedRowKeys.length) {
      message.error('Select containers and a bin location to assign');
      return;
    }
    setAssignLoading(true);
    const res = await dispatch(
      assignContainerBins({
        prod_dispenses_id: dispenseId,
        prod_dispense_label_ids: selectedRowKeys,
        bin_number: selectedBin
      })
    );
    setAssignLoading(false);
    if (res?.payload?.success) {
      message.success(res?.payload?.message);
      await refetchContainers();
      setSelectedRowKeys([]);
      setSelectedBin(undefined);
    } else {
      message.error(res?.payload?.message || 'Failed to assign bin location');
    }
  };

  const allScanned = labels.length > 0 && labels.every((l) => isScanned(l.id));
  const allAssigned =
    labels.length > 0 && labels.every((l) => !!getLocation(l));

  const submitInward = (extra) => {
    const payload = {
      id: dispenseId,
      prod_room_id: roomNum,
      status: 'inward',
      prod_containers: scannedIds,
      ...extra
    };
    setSubmitLoading(true);
    dispatch(createLabels(payload)).then((res) => {
      setSubmitLoading(false);
      if (res?.payload?.success) {
        message.success(res?.payload?.message);
        history.push('/log-books');
      } else {
        message.error(res?.payload?.message || 'Submission failed');
      }
    });
  };

  // Outward and return both submit the checkbox selection, differing only
  // in the status sent to the backend.
  const submitSelected = (status, extra) => {
    const payload = {
      id: dispenseId,
      prod_room_id: roomNum,
      status,
      prod_containers: selectedRowKeys,
      ...extra
    };
    setSubmitLoading(true);
    dispatch(createLabels(payload)).then((res) => {
      setSubmitLoading(false);
      if (res?.payload?.success) {
        message.success(res?.payload?.message);
        history.push('/log-books');
      } else {
        message.error(res?.payload?.message || 'Submission failed');
      }
    });
  };

  // Outward must land the containers in a process area — open the choice modal.
  const handleOutwardMove = (move_to) => {
    setShowMoveModal(false);
    submitSelected('outward', { move_to });
  };

  const onFinish = () => {
    if (typeLower === 'inward') {
      if (roomRequiresVerification) {
        setShowCredModal(true);
      } else {
        submitInward({});
      }
    } else if (typeLower === 'outward') {
      setShowMoveModal(true);
    } else {
      submitSelected('return');
    }
  };

  const onFinishCred = (values) => {
    submitInward({
      employee_id: values?.email,
      password: values?.password,
      comment: values?.comment
    });
    setShowCredModal(false);
  };

  const handleClose = () => {
    history.back();
  };

  const onChangeRoom = (e) => {
    setRoomNum(e.target.value);
  };

  // Outward groups by the first-selected container's location; inward-WH
  // selection is only for assigning bins (scanned containers only).
  const selectedLocation = selectedRowKeys.length
    ? getLocation(labels.find((l) => l.id === selectedRowKeys[0]))
    : null;

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => {
      if (isReturn) {
        // Only scanned containers can be selected; no location grouping.
        return { disabled: !isScanned(record.id) };
      }
      if (typeLower === 'outward') {
        const scanned = isScanned(record.id);
        const diffLocation =
          isLocationBased &&
          selectedLocation &&
          getLocation(record) !== selectedLocation;
        return {
          disabled: !scanned || diffLocation
        };
      }
      // inward WH: assign bins to scanned, not-yet-inwarded containers only
      return { disabled: !isScanned(record.id) || isInwarded(record) };
    }
  };

  const showRowSelection =
    (typeLower === 'inward' && roomRequiresBin) ||
    typeLower === 'outward' ||
    isReturn;
  const showLocationColumn =
    (typeLower === 'inward' && roomRequiresBin) || isLocationBased || isReturn;

  const columns = [
    {
      title: 'Container No.',
      dataIndex: 'prod_containers',
      key: 'prod_containers',
      ellipsis: true,
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md']
    },
    {
      title: 'Lot No.',
      dataIndex: 'lot_no',
      key: 'lot_no',
      ellipsis: true,
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md'],
      render: () => {
        const lot = containersData?.[0]?.lot_no;
        return lot ? lot : '';
      }
    },
    {
      title: 'Quantity',
      dataIndex: 'net_Wt',
      key: 'net_Wt',
      ellipsis: true,
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md']
    },
    ...(scanEnabled
      ? [
          {
            title: 'Scan Status',
            key: 'scan_status',
            ellipsis: true,
            responsive: ['xl', 'xs', 'sm', 'xxl', 'md'],
            render: (_, record) => {
              if (typeLower === 'inward' && isInwarded(record)) {
                return <Tag color="blue">Inwarded</Tag>;
              }
              return isScanned(record.id) ? (
                <Tag color="success">Scanned</Tag>
              ) : (
                <Tag>Pending</Tag>
              );
            }
          }
        ]
      : []),
    ...(showLocationColumn
      ? [
          {
            title: 'Location',
            key: 'location',
            ellipsis: true,
            responsive: ['xl', 'xs', 'sm', 'xxl', 'md'],
            render: (_, record) => getLocation(record) || '-'
          }
        ]
      : []),
    {
      title: 'Hold Time',
      dataIndex: 'hold_time',
      key: 'hold_time',
      ellipsis: true,
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md'],
      render: () => {
        return containersData?.[0]?.hold_time
          ? moment(containersData[0].hold_time).format('DD-MM-YYYY HH:mm')
          : '';
      }
    },
    {
      title: 'Next Stage',
      key: 'next_stage',
      ellipsis: true,
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md'],
      render: () => {
        return containersData?.[0]?.prodlabel_nextstage_master
          ?.prodlabel_nextstage;
      }
    }
  ];

  let locale = {
    emptyText: (
      <span>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <div className={styles.empty_state_text}>
          Scan a container to load the dispense
        </div>
      </span>
    )
  };

  const scannedCount = labels.filter((l) => isScanned(l.id)).length;

  // Submit gating
  let submitDisabled = true;
  let submitReason = '';
  if (!labels.length) {
    submitDisabled = true;
    submitReason = 'Scan a container to load the dispense';
  } else if (typeLower === 'inward') {
    if (alreadyInwarded) {
      submitDisabled = true;
      submitReason = 'These containers are already inwarded';
    } else if (!allScanned) {
      submitDisabled = true;
      submitReason = 'Scan all containers before submitting';
    } else if (roomRequiresBin && !allAssigned) {
      submitDisabled = true;
      submitReason = 'Assign a bin location to every container';
    } else {
      submitDisabled = false;
    }
  } else if (typeLower === 'outward') {
    if (!selectedRowKeys.length) {
      submitDisabled = true;
      submitReason = 'Select scanned containers from a single location';
    } else {
      submitDisabled = false;
    }
  } else {
    // return
    if (!selectedRowKeys.length) {
      submitDisabled = true;
      submitReason = 'Select scanned containers to submit';
    } else {
      submitDisabled = false;
    }
  }

  return (
    <>
      <Layout sider={false}>
        <Card
          className={`${styles.cardStyles_approver}`}
          title={`${type} Register`}
        >
          {typeLower === 'inward' && (
            <>
              <Space>
                <Radio.Group onChange={onChangeRoom} value={roomNum}>
                  {rooms?.map((room, index) => {
                    return (
                      <Radio
                        value={room?.id}
                        key={index}
                        disabled={containersData?.length}
                      >
                        {normalizeRoom(room?.room_no)}
                      </Radio>
                    );
                  })}
                </Radio.Group>
              </Space>
              <br />
              <br />
            </>
          )}

          {isReturn && (
            <Row gutter={24}>
              <Col xs={4} xl={6} span={2} md={2} sm={2}>
                <FormItem label="Room number" className={styles.customLable}>
                  <Input
                    placeholder=""
                    value={labels?.[0]?.prod_room_master?.room_no}
                    disabled
                  />
                </FormItem>
              </Col>
            </Row>
          )}

          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Row gutter={24}>
              <Col xs={4} xl={2} span={2} md={2} sm={2}>
                <Tooltip title="Scan a container to load the dispense">
                  <CustomButton
                    className={styles.qrcode}
                    onClick={handleOpenScan}
                  >
                    <QrcodeOutlined />
                  </CustomButton>
                </Tooltip>
              </Col>
              <Col span={6}>
                <FormItem label="Product name" className={styles.customLable}>
                  <Input
                    placeholder=""
                    value={
                      containersData?.[0]?.product_detail?.name ||
                      containersData?.[0]?.product_name
                    }
                    disabled
                  />
                </FormItem>
              </Col>
              <Col span={4}>
                <FormItem label="Batch Number" className={styles.customLable}>
                  <Input
                    placeholder=""
                    value={containersData?.[0]?.batch_no}
                    disabled
                  />
                </FormItem>
              </Col>
              <Col span={4}>
                <FormItem label="Stage" className={styles.customLable}>
                  <Input
                    placeholder=""
                    value={
                      containersData?.[0]?.prod_label_stage_master
                        ?.prodlabel_stage_name
                    }
                    disabled
                  />
                </FormItem>
              </Col>
              <Col span={4}>
                <FormItem label="Process" className={styles.customLable}>
                  <Input
                    placeholder=""
                    value={
                      containersData?.[0]?.prod_label_process_master
                        ?.process_label_name
                    }
                    disabled
                  />
                </FormItem>
              </Col>
            </Row>

            {scanEnabled && labels.length > 0 && (
              <Row gutter={24} style={{ marginBottom: 12 }} align="middle">
                <Col>
                  <Tooltip
                    title={
                      alreadyInwarded
                        ? 'These containers are already inwarded'
                        : ''
                    }
                  >
                    <CustomButton
                      type="primary"
                      ghost
                      size="small"
                      disabled={alreadyInwarded}
                      onClick={() => setOpenScanChecklist(true)}
                    >
                      Scan Containers ({scannedCount}/{labels.length})
                    </CustomButton>
                  </Tooltip>
                </Col>
                {typeLower === 'inward' && roomRequiresBin && !alreadyInwarded && (
                  <>
                    <Col xs={24} xl={8} md={10} sm={12}>
                      <Select
                        style={{ width: '100%' }}
                        showSearch
                        value={selectedBin}
                        placeholder="Search bin location (e.g. 4-K-3-03)"
                        optionFilterProp="children"
                        onSearch={onSearchbin}
                        onChange={(v) => setSelectedBin(v)}
                        allowClear
                      >
                        {binOptions.map((item, index) => (
                          <Select.Option value={item.bin_number} key={index}>
                            {item.bin_number}
                          </Select.Option>
                        ))}
                      </Select>
                    </Col>
                    <Col>
                      <Tooltip title="Select scanned containers, pick a bin, then assign">
                        <CustomButton
                          type="primary"
                          size="small"
                          loading={assignLoading}
                          disabled={!selectedBin || !selectedRowKeys.length}
                          onClick={handleAssignBin}
                        >
                          Assign Bin
                        </CustomButton>
                      </Tooltip>
                    </Col>
                  </>
                )}
              </Row>
            )}

            <CustomTable
              rowSelection={showRowSelection ? rowSelection : undefined}
              rowKey={'id'}
              locale={locale}
              loading={loading}
              columns={columns}
              dataSource={labels}
              pagination={false}
              className={styles.scrolling}
            />
            <FormItem className="text-center">
              <Space>
                <CustomButton
                  className={styles.inwardButton}
                  onClick={handleClose}
                  type="primary"
                  size="small"
                  ghost
                >
                  Close
                </CustomButton>
                <Tooltip title={submitDisabled ? submitReason : ''}>
                  <CustomButton
                    className={styles.inwardButton}
                    htmlType="submit"
                    type="primary"
                    size="small"
                    loading={submitLoading}
                    disabled={submitDisabled}
                  >
                    Submit
                  </CustomButton>
                </Tooltip>
              </Space>
            </FormItem>
          </Form>
        </Card>

        {showCredModal ? (
          <CustomModal
            title="Initiate verification"
            visible={showCredModal}
            width={'40%'}
            footer={null}
            className={styles.customModal}
            closable={false}
          >
            <Form form={credForm} layout="vertical" onFinish={onFinishCred}>
              <Row gutter={24}>
                <Col xs={24} xl={12} span={24} md={24} sm={24}>
                  <FormItem
                    label="Verified By (User id)"
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: 'Enter Verifier User Id'
                      }
                    ]}
                    className={styles.customLable}
                  >
                    <Input placeholder="Enter Verifier User Id" />
                  </FormItem>
                </Col>
                <Col xs={24} xl={12} span={24} md={24} sm={24}>
                  <FormItem
                    label="Password"
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: 'Enter Password'
                      }
                    ]}
                    className={styles.customLable}
                  >
                    <Input.Password placeholder="Enter Password" />
                  </FormItem>
                </Col>
                <Col xs={24} xl={24} span={24} md={24} sm={24}>
                  <FormItem
                    label="Comments"
                    name="comment"
                    rules={[
                      {
                        required: true,
                        message: 'Enter Comments'
                      }
                    ]}
                    className={styles.customLable}
                  >
                    <Input.TextArea rows={3} placeholder="Enter Comments" />
                  </FormItem>
                </Col>
              </Row>
              <FormItem className="text-center">
                <Space>
                  <CustomButton
                    className={styles.inwardButton}
                    htmlType="submit"
                    type="primary"
                    size="medium"
                    loading={submitLoading}
                  >
                    Submit
                  </CustomButton>
                  <CustomButton
                    className={styles.inwardButton}
                    onClick={() => setShowCredModal(false)}
                    type="primary"
                    ghost
                    size="medium"
                  >
                    Close
                  </CustomButton>
                </Space>
              </FormItem>
            </Form>
          </CustomModal>
        ) : (
          ''
        )}

        {showMoveModal ? (
          <CustomModal
            title="Move outwarded containers to"
            visible={showMoveModal}
            width={'40%'}
            footer={null}
            className={styles.customModal}
            onCancel={() => setShowMoveModal(false)}
          >
            <p style={{ marginBottom: 16 }}>
              Where should the outwarded container(s) go?
            </p>
            <Space>
              <CustomButton
                className={styles.inwardButton}
                type="primary"
                loading={submitLoading}
                onClick={() => handleOutwardMove('Dispensed')}
              >
                Move to Production Area
              </CustomButton>
              <CustomButton
                className={styles.inwardButton}
                type="primary"
                loading={submitLoading}
                onClick={() => handleOutwardMove('Sampled')}
              >
                Move to WIP Area
              </CustomButton>
              <CustomButton
                className={styles.inwardButton}
                type="primary"
                ghost
                onClick={() => setShowMoveModal(false)}
              >
                Cancel
              </CustomButton>
            </Space>
          </CustomModal>
        ) : (
          ''
        )}

        <QrScanSingleShotModal
          title="Scan Container QR"
          visible={openCam}
          onCancel={() => setOpenCam(false)}
          hint="Point the camera at a container QR code."
          onScan={(decodedText) => handleQrScan({ text: decodedText })}
        />

        <QrScanChecklistModal
          title="Scan Containers"
          visible={openScanChecklist}
          onCancel={handleChecklistClose}
          onConfirm={handleChecklistClose}
          targetLabels={labels.map((l) => l.prod_containers)}
          initialConfirmed={labels
            .filter((l) => isScanned(l.id))
            .map((l) => l.prod_containers)}
          onItemConfirmed={handleItemConfirmed}
          alwaysShowChecklist
          hideManualEntry
          confirmButtonText="Done"
          noun={{ singular: 'container', plural: 'containers' }}
        />
      </Layout>
    </>
  );
};

export default registerLogBooks;
